/** @jsxImportSource @emotion/react */
import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import { toast } from '@app/components/ui/Toast/toast'
import { getBaseLayerId } from '@app/domains/layers/utils/layers.utils'
import { SUITABILITY_GRADES, SuitabilityCreateForm } from '@app/domains/suitability/types'
import { useAvailableUseCases } from '@app/domains/useCases/hooks/useAvailableUseCases'
import { useCasesAtom } from '@app/domains/useCases/state/useCasesAtom'
import { userAtom } from '@app/hooks/useUserProfile'
import { UserRole } from '@app/types/userTypes'
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import { DatabaseIcon } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import ReactMarkdown from 'react-markdown'
import { useNavigate, useParams } from 'react-router-dom'
import { useUpsertLayerSuitability } from '../hooks/useUpsertLayerSuitability'
import * as styles from './SuitabilityCreate.styles'

export const SuitabilityCreate = () => {
  const { layerId: rawLayerId } = useParams<{ layerId: string }>()
  const navigate = useNavigate()
  const layerId = rawLayerId ? getBaseLayerId(rawLayerId) : undefined
  const [mode, setMode] = useState<'write' | 'preview'>('write')
  const { mutate: updateLayerSuitability, isPending } = useUpsertLayerSuitability(layerId ?? '')
  const { data: user } = useAtomValue(userAtom)

  const availableUseCases = useAvailableUseCases(layerId ?? '')
  const useCases = useAtomValue(useCasesAtom).data
  const totalUseCases = useCases?.length ?? 0
  const isAdmin = user?.user_roles.includes(UserRole.ADMIN)
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<SuitabilityCreateForm>({
    defaultValues: {
      useCaseSlug: '',
      grade: 'C',
      statementMd: '',
    },
  })

  const statementMd = watch('statementMd')

  const onSubmit = (values: SuitabilityCreateForm) => {
    if (!layerId) return
    updateLayerSuitability(
      {
        useCaseSlug: values.useCaseSlug,
        payload: {
          suitability_grade: values.grade,
          suitability_statement_md: values.statementMd || undefined,
        },
      },
      {
        onSuccess: () => {
          reset()
          toast({ title: 'Suitability saved', type: 'success', position: 'top-center' })
          navigate('..')
        },
        onError: () => {
          toast({ title: 'Failed to save suitability', type: 'error', position: 'top-center' })
        },
      },
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} css={styles.formRoot}>
      <Stack spacing={2} css={styles.formStack}>
        {totalUseCases === 0 && (
          <Box css={styles.emptyStateWrap}>
            <EmptyData
              icon={<DatabaseIcon size={32} />}
              title="No use cases available"
              description="Contact your administrator."
              actionLabel="Go to Settings"
            />
          </Box>
        )}

        {isAdmin && totalUseCases > 0 && availableUseCases.length === 0 && (
          <Box css={styles.emptyStateWrap}>
            <EmptyData
              icon={<DatabaseIcon size={32} />}
              title="All done here"
              description="All enabled use cases already have a suitability assessment. To add more, ask your administrator to enable additional use cases."
              onAction={() => navigate('../settings')}
              actionLabel="Settings"
            />
          </Box>
        )}

        {availableUseCases.length > 0 && (
          <Box css={styles.fieldsSection}>
            <Box css={styles.topFieldsGrid}>
              <Controller
                name="useCaseSlug"
                control={control}
                rules={{ required: 'Use case is required' }}
                render={({ field }) => (
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!errors.useCaseSlug}
                    css={styles.fieldControl}
                  >
                    <InputLabel id="use-case-label" shrink>
                      Use case
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="use-case-label"
                      label="Use case"
                      displayEmpty
                      value={field.value ?? ''}
                      renderValue={(selected) => {
                        if (!selected) {
                          return 'Select a use case'
                        }

                        return (
                          availableUseCases.find((uc) => uc.slug === selected)?.name ?? selected
                        )
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a use case</em>
                      </MenuItem>
                      {availableUseCases.map((uc) => (
                        <MenuItem key={uc.slug} value={uc.slug}>
                          {uc.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.useCaseSlug && (
                      <FormHelperText>{errors.useCaseSlug.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="grade"
                control={control}
                rules={{ required: 'Grade is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Suitability grade"
                    error={!!errors.grade}
                    helperText={errors.grade?.message}
                    css={styles.fieldControl}
                  >
                    {SUITABILITY_GRADES.map((g) => (
                      <MenuItem key={g} value={g}>
                        {g}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Box>

            <Box css={styles.modeSection}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_, val) => val && setMode(val)}
                css={styles.modeToggleGroup}
              >
                <ToggleButton value="write">Write</ToggleButton>
                <ToggleButton value="preview">Preview</ToggleButton>
              </ToggleButtonGroup>

              {mode === 'write' ? (
                <Controller
                  name="statementMd"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={8}
                      label="Suitability statement (Markdown)"
                      css={styles.statementField}
                    />
                  )}
                />
              ) : (
                <Box css={styles.previewPanel}>
                  {statementMd ? (
                    <ReactMarkdown>{statementMd}</ReactMarkdown>
                  ) : (
                    <Box css={styles.previewEmptyState}>Nothing to preview.</Box>
                  )}
                </Box>
              )}
            </Box>

            <Box css={styles.actionsRow}>
              <Button
                type="submit"
                variant="contained"
                disabled={!layerId || isPending}
                css={styles.submitButton}
              >
                Save suitability
              </Button>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  )
}
